import { useCallback, useRef, useState } from 'react';

// Pan/zoom state for the infinite canvas. Returns the transform tuple plus
// the imperative bind helpers. `bindWheel(el)` MUST be called inside a
// useEffect that targets the wrap element directly, because the wheel
// listener has to be non-passive to allow preventDefault during ⌘-zoom.

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.6;
const INITIAL = { x: 40, y: 24, k: 1 };

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

export function useCanvasTransform() {
  const [transform, setTransform] = useState(INITIAL);
  const dragRef = useRef(null);

  const fit = useCallback(() => setTransform(INITIAL), []);

  const zoomBy = useCallback((delta) => {
    setTransform((p) => ({ ...p, k: clamp(p.k + delta, ZOOM_MIN, ZOOM_MAX) }));
  }, []);

  const zoomToK = useCallback((next) => {
    setTransform((p) => ({ ...p, k: clamp(next, ZOOM_MIN, ZOOM_MAX) }));
  }, []);

  // Imperative wheel binding so the listener can be non-passive.
  const bindWheel = useCallback((el) => {
    if (!el) return undefined;
    const handler = (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const factor = 1 + e.deltaY * -0.002;
        setTransform((p) => ({ ...p, k: clamp(p.k * factor, ZOOM_MIN, ZOOM_MAX) }));
      } else {
        setTransform((p) => ({ ...p, x: p.x - e.deltaX, y: p.y - e.deltaY }));
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Drag handlers — return for spread onto the wrap element.
  const onMouseDown = (e) => {
    if (e.target.closest('.node, .sticky, [data-no-pan]')) return;
    dragRef.current = { sx: e.clientX, sy: e.clientY, tx: transform.x, ty: transform.y };
  };
  const onMouseMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    setTransform((p) => ({ ...p, x: drag.tx + (e.clientX - drag.sx), y: drag.ty + (e.clientY - drag.sy) }));
  };
  const onMouseUp = () => { dragRef.current = null; };

  return {
    transform,
    setTransform,
    fit,
    zoomBy,
    zoomToK,
    bindWheel,
    dragHandlers: { onMouseDown, onMouseMove, onMouseUp, onMouseLeave: onMouseUp },
  };
}
